import { Cop31FactBlock, factFaqEntities } from "@/components/Cop31FactBlock";
import type { ReactNode } from "react";
import { localePath } from "@/lib/locale-paths";
import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks } from "@/lib/seo";
import cop31Og from "@/assets/cop31-og.jpg.asset.json";
import { pageTitle } from "@/lib/brand";
import { cop31NoteSlug } from "@/lib/cop31-notes";

const OG_IMAGE_URL = `https://akillisurdurulebilirlik.com${cop31Og.url}`;

export const Route = createFileRoute("/cop31-guide")({
  head: () => ({
    meta: [
      { title: pageTitle("COP31 Antalya 2026: Dates, Venue, Presidency and Agenda", "en") },
      {
        name: "description",
        content:
          "COP31 Antalya 2026: dates (9–20 November), venue (Antalya EXPO), presidency, Leaders' Summit, Pre-COP and agenda — plus independent analysis.",
      },
      { property: "og:title", content: "COP31 Antalya 2026: Dates, Venue, Presidency and Agenda" },
      {
        property: "og:description",
        content:
          "COP31 Antalya 2026: dates (9–20 November), venue (Antalya EXPO), presidency, Leaders' Summit, Pre-COP and agenda — plus independent analysis.",
      },
      { property: "og:type", content: "article" },
      { property: "article:published_time", content: "2026-05-01T00:00:00Z" },
      { property: "article:modified_time", content: "2026-08-25T00:00:00Z" },
      { property: "article:section", content: "Climate" },
      { property: "article:tag", content: "COP31" },
      { property: "article:tag", content: "Climate Policy" },
      { property: "article:tag", content: "Antalya 2026" },
      { property: "og:url", content: "https://smartsustainability.ai/cop31-guide" },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "tr_TR" },
      { property: "og:image", content: OG_IMAGE_URL },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "COP31 Antalya 2026: Dates, Venue, Presidency and Agenda" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE_URL },
    ],
    links: [
      { rel: "canonical", href: "https://smartsustainability.ai/cop31-guide" },
      ...hreflangLinks("/cop31", "/cop31-guide"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Event",
          name: "COP31 — UN Climate Change Conference 2026",
          alternateName: ["COP31 Antalya", "CMA8", "CMP21"],
          description:
            "The 31st Conference of the Parties to the UN Framework Convention on Climate Change, held with CMP21 and CMA8, under Türkiye's presidency in Antalya.",
          image: [OG_IMAGE_URL],
          startDate: "2026-11-09",
          endDate: "2026-11-20",
          eventStatus: "https://schema.org/EventScheduled",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          inLanguage: ["en", "tr"],
          location: {
            "@type": "Place",
            name: "Antalya EXPO Center, Aksu — Antalya, Türkiye",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Antalya",
              addressRegion: "Antalya",
              addressCountry: "TR",
            },
          },
          organizer: [
            {
              "@type": "Organization",
              name: "UNFCCC — United Nations Framework Convention on Climate Change Secretariat",
              url: "https://unfccc.int",
              sameAs: ["https://unfccc.int/cop31"],
            },
            {
              "@type": "GovernmentOrganization",
              name: "Republic of Türkiye — Ministry of Environment, Urbanisation and Climate Change",
              url: "https://csb.gov.tr",
              sameAs: ["https://iklim.gov.tr", "https://cop31.tr"],
            },
          ],
          sameAs: [
            "https://unfccc.int/cop31",
            "https://cop31.tr",
            "https://en.wikipedia.org/wiki/2026_United_Nations_Climate_Change_Conference",
          ],
          url: "https://smartsustainability.ai/cop31-guide",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://akillisurdurulebilirlik.com/" },
            { "@type": "ListItem", position: 2, name: "Climate & Systems", item: "https://smartsustainability.ai/climate-systems" },
            { "@type": "ListItem", position: 3, name: "COP31 Guide", item: "https://smartsustainability.ai/cop31-guide" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: "en",
          mainEntity: [
            ...factFaqEntities("en"),
            {
              "@type": "Question",
              name: "When and where is COP31?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "COP31 takes place 9–20 November 2026 at Antalya EXPO Center (Aksu, Antalya), under Türkiye's presidency.",
              },
            },
            {
              "@type": "Question",
              name: "What is COP31?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "COP31 is the 31st Conference of the Parties to the UN Framework Convention on Climate Change. It is held alongside the 21st Meeting of the Parties to the Kyoto Protocol (CMP21) and the 8th Meeting of the Parties to the Paris Agreement (CMA8).",
              },
            },
            {
              "@type": "Question",
              name: "Who holds the COP31 presidency?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Türkiye holds the COP31 presidency; the President is Minister of Environment, Urbanisation and Climate Change Murat Kurum. Under the Türkiye–Australia Partnership Modalities, Australia works as a close negotiating partner, and the Pre-COP will take place in a Pacific country.",
              },
            },
            {
              "@type": "Question",
              name: "How can I attend COP31?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "The venue has two zones. The Blue Zone (the official UNFCCC negotiation area) is accreditation-only for party delegations, registered observer organisations, UN agencies, and accredited media. The Green Zone is open to the public; registration is online via cop31.tr.",
              },
            },
            {
              "@type": "Question",
              name: "What are the official COP31 information sources?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Internationally: unfccc.int/cop31. Turkish presidency: cop31.tr, with institutional announcements at iklim.gov.tr and csb.gov.tr. Sites carrying the COP31 name outside these are unofficial; accreditation and registration should be processed only through the addresses above.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: Cop31EnPage,
});


function SourceLink({ label, url }: { label: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener"
      className="block text-xs text-muted-foreground hover:text-clay"
    >
      <span className="text-muted-foreground/70">Source: </span>
      <span className="underline underline-offset-2">{label}</span>
    </a>
  );
}

function SectionTitle({ kicker, title }: { kicker?: string; title: string }) {
  return (
    <header className="mb-6">
      {kicker && (
        <p className="mb-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
          {kicker}
        </p>
      )}
      <h2 className="font-serif text-2xl leading-tight md:text-3xl">{title}</h2>
    </header>
  );
}

function Cop31EnPage() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 md:py-20">
      {/* Hero */}
      <header className="mb-16">
        <div className="mb-4 flex items-center justify-between gap-4">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            COP31 · Antalya · 9–20 November 2026
          </p>
          {/* Language switcher — deliberate cross-locale link. locale-link-ok */}
          <Link
            to={localePath("cop31", "tr")}
            className="text-xs uppercase tracking-[0.2em] text-clay underline underline-offset-4 hover:no-underline"
          >
            TR
          </Link>
        </div>
        <h1 className="mb-6 font-serif text-4xl leading-tight md:text-5xl">
          COP31 Antalya: Reading Between the Lines of the Negotiation
        </h1>
        <p className="mb-4 text-lg leading-relaxed text-foreground/85 md:text-xl">
          The 31st Conference of the Parties to the UN Framework Convention on Climate
          Change convenes 9–20 November 2026 in Antalya, under Türkiye's presidency.
          This page does two things: it presents verified essentials about the conference
          with their official sources, and it reads the invisible layers of the
          negotiations — finance architecture, the water agenda, the commitment-to-
          implementation gap — through the Smart Sustainability lens.
        </p>
        <p className="font-serif text-lg italic text-clay md:text-xl">
          Formalities live elsewhere; analysis lives here.
        </p>
      </header>

      <Cop31FactBlock lang="en" />

      {/* What/When/Where */}
      <section className="mb-16">
        <SectionTitle title="What is COP31, When and Where?" />
        <p className="mb-6 leading-relaxed text-foreground/85">
          COP31 is not a single conference but the collective name for three official
          sessions held under one roof: the 31st Conference of the Parties to the
          Convention (COP31), the 21st Meeting of the Parties to the Kyoto Protocol
          (CMP21), and the 8th Meeting of the Parties to the Paris Agreement (CMA8).
          The three negotiation tracks run in parallel; the Paris Agreement's
          implementation rules are shaped on the CMA track.
        </p>
        <dl className="mb-6 divide-y divide-border rounded-md border border-border bg-card">
          <div className="grid gap-1 p-4 sm:grid-cols-[10rem_1fr]">
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Dates</dt>
            <dd className="text-sm">9–20 November 2026</dd>
          </div>
          <div className="grid gap-1 p-4 sm:grid-cols-[10rem_1fr]">
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Venue</dt>
            <dd className="text-sm">Antalya EXPO Center, Aksu — Antalya, Türkiye</dd>
          </div>
          <div className="grid gap-1 p-4 sm:grid-cols-[10rem_1fr]">
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">
              Host & Presidency
            </dt>
            <dd className="text-sm">
              Türkiye (COP31 President: Minister of Environment, Urbanisation and
              Climate Change, Murat Kurum)
            </dd>
          </div>
          <div className="grid gap-1 p-4 sm:grid-cols-[10rem_1fr]">
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">
              Negotiation partnership
            </dt>
            <dd className="text-sm">
              Under the Türkiye–Australia Partnership Modalities, Australia works
              closely with the incoming presidency; the Pre-COP will take place in a
              Pacific country.
            </dd>
          </div>
        </dl>
        <div className="space-y-1">
          <SourceLink label="UNFCCC — Official COP31 Page" url="https://unfccc.int/cop31" />
          <SourceLink label="Turkish Directorate of Climate Change" url="https://iklim.gov.tr" />
        </div>
      </section>


      {/* Analysis Layer */}
      <section className="mb-8">
        <SectionTitle
          kicker="Analysis Layer"
          title="Reading COP31 Through the Smart Sustainability Lens"
        />
        <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
          The pieces below were originally published in Turkish in Ekonomi Gazetesi.
          English summaries appear here, with complete English essays on their note pages.
        </p>
        <div className="space-y-6">
          <AnalizCard
            slug="cop31-iklimin-siyasi-ekonomisi"
            title="COP31: The Political Economy of Climate"
            date="7 October 2026"
          >
            The Paris Agreement built climate diplomacy around carbon. Antalya
            will have to rethink it through power. Because the world on which
            Paris was built no longer exists.
          </AnalizCard>
          <AnalizCard
            slug="antalya-dan-once-son-durak-pasifik"
            title="The Last Stop Before Antalya: The Pacific"
            date="1 October 2026"
          >
            Fiji, Tuvalu and Australia will co-host the official Pre-COP process
            on 5–8 October. The Pacific is carrying three clear priorities to
            Antalya: keeping 1.5°C on the table, securing fair access to climate
            finance, and moving the ocean onto the formal negotiating agenda.
            The complete essay is available in English on the note page.
          </AnalizCard>
          <AnalizCard
            slug="new-yorkta-iklimin-yeni-dili"
            title="The New Language of Climate in New York"
            date="30 September 2026"
          >
            At New York Climate Week, Türkiye set out an implementation agenda
            for COP31 spanning electrification, artificial intelligence,
            finance and cities. This time, the private sector was also at the
            table. On the road to Antalya, the central question is no longer
            what will be promised, but what can actually be implemented. Full
            essay in English on the note page.
          </AnalizCard>
          <AnalizCard
            slug="cop31-in-onundeki-yeni-fay-hatti"
            title="The New Fault Line Ahead of COP31"
            date="18 September 2026"
          >
            The United States treats carbon as a burden to be lifted off the
            economy; Europe is turning it into a new entry condition for
            competition. The world is splitting into two economic orders around
            the price of carbon. The American seat at COP31 may stay empty, but
            the policy of making carbon cheap will sit at the centre of the
            table. Full essay in English on the note page.
          </AnalizCard>
          <AnalizCard
            slug="iklim-hedefinden-rekabet-avantajina"
            title="From Climate Target to Competitive Advantage"
            date="9 September 2026"
            url="https://www.ekonomigazetesi.com/kose-yazisi/iklim-hedefinden-rekabet-avantajina-87087"
          >
            Climate transition now sits at the centre of economic competitiveness,
            not only environmental policy. At the Istanbul Climate Finance Summit,
            the Türkiye COP31 Presidency's Climate Implementation Bridge (BRIDGE)
            targeted the disconnect between targets and finance: the missing
            pipeline of investable projects. Against an annual need of $7.5–9
            trillion, actual investment stands near $1.9 trillion — a gap of
            predictable policy and structured projects as much as of money.
          </AnalizCard>
          <AnalizCard

            slug="from-chasm-to-bridge"
            title="From Chasm to Bridge: The New Language of Climate Finance"
            date="6 September 2026"
          >
            The Istanbul Climate Finance Summit produced two words: a chasm and a
            bridge. They belong to two different regimes of persuasion — the chasm
            speaks to consciences, the bridge to committees. Part of the climate
            finance gap is not a shortage of money but a shortage of translation
            between them.
          </AnalizCard>
          <AnalizCard

            slug="the-overshoot-cop"
            title="The Overshoot COP"
            date="2 September 2026"
            url="https://www.unep.org/resources/limiting-overshoot-navigating-exceedance"
            sourceLabel="UNEP report ↗"
          >
            The world will cross 1.5°C. The new question is not only whether the
            target survives, but how high the overshoot goes, how long it lasts, and
            who is responsible for closing the window. COP31's promise to be an
            implementation COP will be tested by whether Antalya can assign that
            liability a name, a date, and a repayment schedule.
          </AnalizCard>
          <AnalizCard
            slug="karbon-piyasasi-nedir"
            title="What Is a Carbon Market?"
            date="31 August 2026"
            url="/yazilar/karbon-piyasasi-nedir"
          >
            Türkiye's Emissions Trading System (ETS) regulation is now in force. The
            state caps total emissions and issues allowances — one tonne of CO2 each;
            a company that exceeds its cap has to buy the shortfall, while one that
            cuts emissions can sell what it does not use. Part of the allowances are
            allocated for free, the rest auctioned in the primary market, then traded
            between companies in the secondary market. It looks like an exchange, but
            the scarcity is deliberate: a market stability reserve and price floors
            and ceilings keep the signal usable. Ahead of Antalya, this is the host
            country's most concrete mitigation instrument.
          </AnalizCard>
          <AnalizCard
            slug="cop31-two-countries-two-roles-one-table"
            title="COP31: Two Countries, Two Roles, One Table"
            date="25 August 2026"
          >
            Antalya turns a diplomatic compromise into a governance experiment:
            Türkiye runs the political stage, while Australia runs the negotiating
            table. The final procedural power, however, remains with the Turkish
            Presidency. COP31 will test whether the power to convene and the power
            to negotiate can sit in different hands without the agreement falling
            between them.
          </AnalizCard>
          <AnalizCard
            slug="mavi-karbon-nedir"

            title="What Is Blue Carbon?"
            date="19 August 2026"
            url="https://www.ekonomigazetesi.com/kose-yazisi/mavi-karbon-nedir-85726"
          >
            When a seagrass meadow is damaged we do not only lose a living habitat;
            we also put at risk the carbon accumulated there over decades. Once the
            ecosystem is disturbed, organic carbon in the sediment breaks down again
            and part of it returns to the water or the atmosphere. That makes blue
            carbon a quiet but critical COP31 file: coastal ecosystems count on both
            the adaptation and the mitigation side — and without measurement and
            protection, the loss is irreversible.
          </AnalizCard>
          <AnalizCard
            slug="cop31e-uc-ay-kala"
            title="Three Months to COP31: Where Things Actually Stand"

            date="12 August 2026"
            url="https://www.ekonomigazetesi.com/kose-yazisi/cop31e-uc-ay-kala-tablo-nasil-85191"
          >
            The shape of the 9–20 November summit in Antalya is now largely clear:
            Türkiye holds the Presidency as host, while Australia serves as President
            of the Negotiations. Pre-COP31 moves to Fiji on 5–8 October, with a
            separate leaders' gathering in Tuvalu — a preparation track running along
            a Mediterranean–Pacific line. Bonn failed to converge on the Global Goal
            on Adaptation: indicators, methodology, and the link to finance all stayed
            open, so those files travel to Antalya unresolved. Two agendas therefore
            sit side by side: the formal negotiations, and the implementation and
            investment agenda carried by Türkiye's Action Agenda.
          </AnalizCard>
          <AnalizCard
            slug="yesil-aklama-ispat-meselesi"

            title="Greenwashing Is No Longer an Ethics Question — It Is a Burden of Proof"
            date="7 August 2026"
            url="/yazilar/yesil-aklama-artik-ahlak-degil-ispat-meselesi"
          >
            As of 1 August, claims such as "nature-friendly", "carbon neutral", or
            "sustainable" require evidence in Türkiye. Greenwashing has moved from
            moral criticism to legal proof — and that shift sits underneath COP31:
            the credibility of a pledge will now be measured by the evidence behind
            the claim.
          </AnalizCard>
          <AnalizCard
            slug="yesil-finans-stratejisi"
            title="What Does the Green Finance Strategy Actually Say?"
            date="8 July 2026"
            url="https://www.ekonomigazetesi.com/kose-yazisi/yesil-finans-stratejisi-ne-diyor-82719"
          >
            Read alongside the Climate Law, Türkiye's published green finance strategy
            and action document is the host country's strongest diplomatic instrument
            at COP31. The document's claim to become a "model country" only holds if
            it is carried by that level of concreteness — otherwise it stays a
            brochure sentence.
          </AnalizCard>

          <AnalizCard
            slug="su-gidince"
            title="When the Water Is Gone"
            date="1 July 2026"
            url="https://www.ekonomigazetesi.com/kose-yazisi/su-gidince-82218"
          >
            A childhood memory of a small lake 45 minutes from Ankara — shaded, still,
            quiet. Years later the road is the same, the turns are the same, but the
            lake is gone: a cracked hollow, a dry bed. Water left, and with it,
            everyone.
          </AnalizCard>
          <AnalizCard
            slug="yikici-bilanco-30a-1"
            title="A Ruinous Balance Sheet: 30 to 1"
            date="6 May 2026"
            url="https://www.ekonomigazetesi.com/kose-yazisi/yikici-bilanco-30a-1-78675"
          >
            For every dollar spent protecting nature, roughly thirty dollars flow to
            activities that destroy it. USD 7.3 trillion in destructive finance meets
            only USD 220 billion in protective investment (UNEP, State of Finance for
            Nature 2026). The problem is not just growth priorities; it is
            institutional design — sustainability must interrogate how the system
            values nature.
          </AnalizCard>
          <AnalizCard
            slug="cografya-kader-degil-strateji"
            title="Geography Is Not Destiny, It Is Strategy"
            date="29 April 2026"
            url="https://www.ekonomigazetesi.com/kose-yazisi/cografya-kader-degil-stratejidir-78122"
          >
            Türkiye sits at the intersection of three climate-crisis fronts:
            Mediterranean climate vulnerability, energy security, and finance. COP31
            gives Türkiye an opening to turn that vulnerability into a coherent
            regional framework — and to lead it.
          </AnalizCard>
          <AnalizCard
            slug="kirilgan-yasam-ucgeni"
            title="The Fragile Triangle: Water, Energy, Agriculture"
            date="25 March 2026"
            url="https://www.ekonomigazetesi.com/kose-yazisi/kirilgan-yasam-ucgeni-su-enerji-tarim-75429"
          >
            Türkiye's National Water Plan (2026–2035) is out: targets, strategies,
            actions across efficiency, saving, digitalisation, coordination. The aim
            is sustainable management of water's ecological, economic, and social
            dimensions — because the topic is never just water; it is energy, food,
            and ecosystems.
          </AnalizCard>
        </div>
        <div className="mt-8 rounded-md border border-border/60 bg-muted/30 p-5 md:p-6">
          <p className="mb-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
            More on the site
          </p>
          <p className="mb-4 text-sm leading-relaxed text-foreground/85 md:text-base">
            The structural argument behind COP31 — water governance, energy security,
            finance architecture, and the commitment-to-implementation gap — is
            developed in the climate & systems dossier.
          </p>
          <Link
            to={localePath("climate", "en")}
            className="inline-block text-sm font-semibold uppercase tracking-[0.15em] text-clay underline underline-offset-4 hover:no-underline"
          >
            All climate essays → Climate &amp; Systems
          </Link>
        </div>
      </section>

      {/* Cross-link to the Turkish page */}
      <section className="mb-8 rounded-md border border-border/60 bg-muted/30 p-5 md:p-6">
        <p className="mb-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Also in Turkish
        </p>
        <p className="mb-4 text-sm leading-relaxed text-foreground/85 md:text-base">
          The same guide is maintained in Turkish, with the original columns quoted
          at length.
        </p>
        {/* Deliberate cross-locale link to the Turkish edition. locale-link-ok */}
        <Link
          to={localePath("cop31", "tr")}
          className="inline-block text-sm font-semibold uppercase tracking-[0.15em] text-clay underline underline-offset-4 hover:no-underline"
        >
          Read this page in Turkish →
        </Link>
      </section>


    </article>

  );
}

function AnalizCard({
  title,
  date,
  url,
  sourceLabel,
  slug,
  children,
}: {
  title: string;
  date: string;
  url?: string;
  sourceLabel?: string;
  slug: string;
  children: ReactNode;
}) {
  return (
    <article className="rounded-md border border-border bg-card p-5 md:p-6">
      <p className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
        {date}
      </p>
      <h3 className="mb-3 font-serif text-xl leading-snug md:text-2xl">
        <Link
          to="/cop31-guide/notes/$slug"
          params={{ slug: cop31NoteSlug(slug, "en") }}
          className="hover:text-clay transition-colors"
        >
          {title}
        </Link>
      </h3>
      <p className="mb-4 text-sm leading-relaxed text-foreground/85 md:text-base">
        {children}
      </p>
      <div className="flex flex-wrap items-center gap-5">
        <Link
          to="/cop31-guide/notes/$slug"
          params={{ slug: cop31NoteSlug(slug, "en") }}
          className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-clay underline underline-offset-4 hover:no-underline"
        >
          Open the note →
        </Link>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener"
            className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground underline underline-offset-4 hover:no-underline"
          >
            {sourceLabel ?? "Source (Turkish) ↗"}
          </a>
        )}
      </div>

    </article>
  );
}
